import type { Article } from "../insights";
import { ctaPl, ctaEn, ctaZh } from "../insight-cta";

export const rekrutacjaItWarszawa: Article = {
  slug: "rekrutacja-it-warszawa",
  updated: "2026-08-18",
  updatedLabel: {
    pl: "Aktualizacja: 18 sierpnia 2026",
    en: "Last updated: 18 August 2026",
    zh: "最近更新：2026 年 8 月 18 日",
  },
  topic: { pl: "Warszawa", en: "Warsaw", zh: "华沙" },
  pl: {
    title: "Rekrutacja IT w Warszawie: benchmark stawek i konkurencja",
    h1: "Rekrutacja IT w Warszawie 2026: stawki, konkurencja, proces",
    metaTitle: "Rekrutacja IT Warszawa 2026: stawki, pula, czas obsadzenia",
    metaDescription:
      "Rekrutacja IT w Warszawie 2026: najwyższe widełki w Polsce, kto konkuruje o kandydata (fintech, banki, scale-upy), ile trwa proces i jak wygrać ofertę bez przepłacania.",
    eyebrow: "Insights · Warszawa",
    summary:
      "Warszawa wyznacza benchmark stawek w Polsce. Kto tu konkuruje o kandydata, ile realnie kosztuje senior i co decyduje o wygranej ofercie.",
    lead:
      "Warszawa to największy rynek IT w Polsce i punkt odniesienia dla widełek w całym kraju. Rekrutujemy tu z Krakowa na co dzień, więc poniżej pokazujemy realia stołecznych procesów w 2026: kto jest konkurencją o tego samego kandydata, jak wysoko trzeba ustawić widełki i dlaczego oferty przegrywają nawet wtedy, gdy są najwyższe finansowo.",
    note:
      "Kwoty miesięczne w PLN. UoP to brutto (koszt pracodawcy ok. +20%), B2B to wartość faktury netto bez VAT. Zakresy planistyczne z naszych procesów 2025-2026.",
    readTime: "8 min czytania",
    takeaways: [
      "Warszawa to ok. 120 tys. specjalistów IT - największa pula w Polsce i benchmark stawek (100%).",
      "Senior software engineer w Warszawie: 18 000 - 28 000 PLN brutto na UoP lub 22 000 - 34 000 PLN netto na B2B.",
      "Fintech i banki płacą zwykle 10-15% powyżej średniej rynkowej za ten sam profil.",
      "Kandydat w Warszawie prowadzi średnio 2-3 procesy równolegle, więc decyzja w 5 dni roboczych bije podniesienie oferty o 2 000 PLN.",
      "Podanie widełek w pierwszej wiadomości mniej więcej podwaja odsetek odpowiedzi kandydatów pasywnych.",
      "Realny czas od rozmowy wprowadzającej do zaakceptowanej oferty w Warszawie: 3-5 tygodni; okres wypowiedzenia dokłada 1-3 miesiące na UoP.",
    ],
    sections: [
      {
        id: "konkurencja",
        eyebrow: "01 / Konkurencja",
        title: "Z kim realnie konkurujesz o warszawskiego kandydata",
        blocks: [
          {
            kind: "table",
            caption: "Typy pracodawców na warszawskim rynku IT, 2026",
            head: ["Typ pracodawcy", "Poziom stawek", "Czym wygrywa", "Gdzie przegrywa"],
            rows: [
              ["Banki i fintech", "+10 do +15%", "Budżet, stabilność, skala danych", "Długi proces, compliance, legacy"],
              ["Scale-upy produktowe", "Rynek do +10%", "Wpływ na produkt, tempo, ESOP", "Ryzyko, mniejsze świadczenia pozapłacowe"],
              ["Centra R&D globalnych firm", "Rynek", "Marka, świadczenia pozapłacowe, uporządkowany proces", "Wolna decyzyjność, wąski zakres"],
              ["Software house'y / consulting", "-5 do -10%", "Różnorodność projektów, szybki start", "Rotacja projektów, praca u klienta"],
              ["Firmy zagraniczne zdalnie (EUR)", "+15 do +30%", "Stawka, pełne zdalne", "Strefy czasowe, brak lokalnego zespołu"],
              ["Sektor publiczny / e-administracja", "-10 do -20%", "Stabilność, misja", "Widełki, tempo, technologia"],
            ],
          },
          {
            kind: "p",
            text: "Warszawski kandydat porównuje twoją ofertę nie z rynkiem lokalnym, tylko z ofertą zdalną w EUR. To nie znaczy, że musisz przebić stawkę - znaczy, że musisz mieć jasny powód, dla którego twoja rola jest ciekawsza: zakres odpowiedzialności, skala systemu, konkretne problemy do rozwiązania.",
          },
        ],
      },
      {
        id: "widelki",
        eyebrow: "02 / Widełki",
        title: "Ile kosztuje inżynier w Warszawie",
        blocks: [
          {
            kind: "table",
            caption: "Widełki wynagrodzeń w Warszawie, 2026",
            head: ["Rola", "UoP (brutto/mc)", "B2B (netto/mc)", "Stawka godzinowa B2B"],
            rows: [
              ["Mid software engineer", "12 000 - 18 500 PLN", "14 000 - 22 000 PLN", "105 - 150 PLN"],
              ["Senior software engineer", "18 000 - 28 000 PLN", "22 000 - 34 000 PLN", "160 - 240 PLN"],
              ["Senior DevOps / SRE", "22 000 - 30 000 PLN", "26 000 - 36 000 PLN", "190 - 250 PLN"],
              ["Senior data engineer", "21 000 - 30 000 PLN", "25 000 - 35 000 PLN", "180 - 245 PLN"],
              ["ML / GenAI engineer (senior)", "24 000 - 34 000 PLN", "28 000 - 40 000 PLN", "200 - 280 PLN"],
              ["Engineering manager", "26 000 - 38 000 PLN", "30 000 - 44 000 PLN", "-"],
            ],
          },
          {
            kind: "p",
            text: "Górna połowa widełek jest w Warszawie realna tylko wtedy, gdy potrafisz uzasadnić poziom roli: własność obszaru, skala ruchu, wpływ na architekturę. Sam tytuł „senior” nie kupuje już nikogo - kandydaci z tego rynku pytają o zakres decyzji, zanim zapytają o pieniądze.",
          },
        ],
      },
      {
        id: "oferta",
        eyebrow: "03 / Oferta",
        title: "Co decyduje o wygranej ofercie w Warszawie",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "Tempo decyzji",
                meta: "Do 5 dni roboczych po ostatniej rozmowie",
                body: "W rynku, gdzie kandydat ma 2-3 procesy naraz, szybkość działa mocniej niż dodatkowe 2 000 PLN. Decyzja po 10+ dniach kosztuje około połowy finalistów.",
              },
              {
                title: "Widełki od pierwszej wiadomości",
                meta: "Podwaja odsetek odpowiedzi",
                body: "„Konkurencyjne wynagrodzenie” jest w Warszawie sygnałem ostrzegawczym. Konkretny przedział filtruje niedopasowania w 30 sekund.",
              },
              {
                title: "Skrócony proces",
                meta: "2-3 spotkania, nie 5",
                body: "Każdy dodatkowy etap to około 15-20% odpadu z lejka. Zadanie domowe powyżej 3 godzin praktycznie eliminuje kandydatów pasywnych.",
              },
              {
                title: "Konkretny zakres roli",
                meta: "Co posiadasz, a nie „ciekawe wyzwania”",
                body: "Opis realnego problemu - migracja, skalowanie, nowy zespół, budżet R&D - odróżnia ofertę od dziesięciu podobnych w tym samym tygodniu.",
              },
            ],
          },
        ],
      },
      {
        id: "proces",
        eyebrow: "04 / Proces",
        title: "Harmonogram: od rozmowy wprowadzającej do startu",
        blocks: [
          {
            kind: "table",
            caption: "Typowy harmonogram rekrutacji IT w Warszawie",
            head: ["Etap", "Czas", "Uwagi"],
            rows: [
              ["Brief i kalibracja widełek", "2 - 3 dni", "Weryfikacja, czy budżet spina się z rynkiem"],
              ["Direct search i rozmowy wstępne", "5 - 10 dni", "120-180 zmapowanych profili na rolę senior"],
              ["Krótka lista", "do 14 dni od rozmowy wprowadzającej", "4 - 6 kandydatów gotowych do decyzji"],
              ["Proces po stronie klienta", "5 - 12 dni", "Optymalnie 2-3 spotkania"],
              ["Oferta i decyzja", "3 - 5 dni", "Kontroferta obecnego pracodawcy to norma"],
              ["Start", "+1 - 3 mies. (UoP) lub 2 - 4 tyg. (B2B)", "Warto planować kontakt w okresie wypowiedzenia"],
            ],
          },
          {
            kind: "p",
            text: "Kontroferta jest w Warszawie standardem, nie wyjątkiem. Rozmowa o powodach zmiany na wczesnym etapie - a nie dopiero przy ofercie - jest najtańszym sposobem, by nie stracić kandydata w ostatnim tygodniu.",
          },
        ],
      },
    ],
    faqTitle: "Najczęstsze pytania: rekrutacja IT w Warszawie",
    faq: [
      {
        q: "Ile zarabia senior developer w Warszawie w 2026?",
        a: "Zwykle 18 000 - 28 000 PLN brutto na umowie o pracę lub 22 000 - 34 000 PLN netto na B2B, zależnie od zestawie technologii, skali systemu i sektora.",
      },
      {
        q: "Dlaczego rekrutacja w Warszawie jest trudniejsza mimo największej puli?",
        a: "Bo konkurencja o uwagę jest największa: kandydat prowadzi 2-3 procesy równolegle i porównuje twoją ofertę również z ofertami zdalnymi w EUR.",
      },
      {
        q: "Czy warto podawać widełki w ogłoszeniu?",
        a: "Tak. W naszych warszawskich procesach podanie przedziału w pierwszej wiadomości mniej więcej podwaja odsetek odpowiedzi kandydatów pasywnych.",
      },
      {
        q: "Ile trwa obsadzenie roli IT w Warszawie?",
        a: "3-5 tygodni od rozmowy wprowadzającej do zaakceptowanej oferty, plus okres wypowiedzenia: 1-3 miesiące na UoP lub 2-4 tygodnie na B2B.",
      },
    ],
    cta: ctaPl,
    methodology:
      "Zakresy pochodzą z procesów rekrutacyjnych Positive Recruitment prowadzonych na rynku warszawskim w latach 2025-2026 i są konfrontowane z publicznymi raportami płacowymi polskiego IT.",
  },
  en: {
    title: "IT recruitment in Warsaw: the pay benchmark and the competition",
    h1: "IT recruitment in Warsaw 2026: pay, competition and process",
    metaTitle: "IT recruitment Warsaw 2026: pay benchmark, pool, timelines",
    metaDescription:
      "IT recruitment in Warsaw 2026: Poland's highest salary ranges, who competes for the same candidate (fintech, banks, scale-ups), how long hiring takes and how to win offers without overpaying.",
    eyebrow: "Insights · Warsaw",
    summary:
      "Warsaw sets Poland's pay benchmark. Who competes for the candidate, what a senior really costs and what decides the winning offer.",
    lead:
      "Warsaw is Poland's largest tech market and the reference point for salary ranges nationwide. We recruit into it daily from Kraków, so here is what Warsaw processes look like in 2026: who else is chasing your candidate, how high the range has to sit and why offers lose even when they pay the most.",
    note:
      "Monthly amounts in PLN. Employment (UoP) is gross (employer cost roughly +20%); B2B is net invoice value excluding VAT. Planning ranges from our 2025-2026 processes.",
    readTime: "8 min read",
    takeaways: [
      "Warsaw hosts roughly 120,000 IT professionals - Poland's largest pool and the pay benchmark (100%).",
      "Senior software engineer in Warsaw: 18,000-28,000 PLN gross on employment or 22,000-34,000 PLN net on B2B.",
      "Banks and fintech typically pay 10-15% above the market average for the same profile.",
      "Warsaw candidates run 2-3 processes in parallel, so deciding within 5 working days beats adding 2,000 PLN to the offer.",
      "Publishing a salary range in the first message roughly doubles passive-candidate reply rates.",
      "Brief to accepted offer takes 3-5 weeks; notice periods add 1-3 months on employment contracts.",
    ],
    sections: [
      {
        id: "konkurencja",
        eyebrow: "01 / Competition",
        title: "Who you are really competing with in Warsaw",
        blocks: [
          {
            kind: "table",
            caption: "Employer types on the Warsaw IT market, 2026",
            head: ["Employer type", "Pay level", "Wins on", "Loses on"],
            rows: [
              ["Banks and fintech", "+10 to +15%", "Budget, stability, data scale", "Long process, compliance, legacy"],
              ["Product scale-ups", "Market to +10%", "Product impact, speed, ESOP", "Risk, thinner benefits"],
              ["Global R&D centres", "Market", "Brand, benefits, structured process", "Slow decisions, narrow scope"],
              ["Software houses / consulting", "-5 to -10%", "Project variety, fast start", "Project churn, client-site work"],
              ["Foreign remote employers (EUR)", "+15 to +30%", "Rate, fully remote", "Time zones, no local team"],
              ["Public sector", "-10 to -20%", "Stability, mission", "Ranges, pace, technology"],
            ],
          },
          {
            kind: "p",
            text: "A Warsaw candidate benchmarks your offer against a EUR-denominated remote job, not against the local market. That does not mean you must outbid it - it means you need a clear reason your role is more interesting: ownership, system scale, concrete problems to solve.",
          },
        ],
      },
      {
        id: "widelki",
        eyebrow: "02 / Pay",
        title: "What an engineer costs in Warsaw",
        blocks: [
          {
            kind: "table",
            caption: "Warsaw salary ranges, 2026",
            head: ["Role", "Employment (gross/mo)", "B2B (net/mo)", "B2B hourly rate"],
            rows: [
              ["Mid software engineer", "12,000 - 18,500 PLN", "14,000 - 22,000 PLN", "105 - 150 PLN"],
              ["Senior software engineer", "18,000 - 28,000 PLN", "22,000 - 34,000 PLN", "160 - 240 PLN"],
              ["Senior DevOps / SRE", "22,000 - 30,000 PLN", "26,000 - 36,000 PLN", "190 - 250 PLN"],
              ["Senior data engineer", "21,000 - 30,000 PLN", "25,000 - 35,000 PLN", "180 - 245 PLN"],
              ["ML / GenAI engineer (senior)", "24,000 - 34,000 PLN", "28,000 - 40,000 PLN", "200 - 280 PLN"],
              ["Engineering manager", "26,000 - 38,000 PLN", "30,000 - 44,000 PLN", "-"],
            ],
          },
          {
            kind: "p",
            text: "The upper half of the range only holds in Warsaw if you can justify the level: area ownership, traffic scale, architectural influence. The word \"senior\" alone buys nobody here - candidates ask about decision scope before they ask about money.",
          },
        ],
      },
      {
        id: "oferta",
        eyebrow: "03 / Offer",
        title: "What decides a winning offer in Warsaw",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "Decision speed",
                meta: "Within 5 working days of the last interview",
                body: "In a market where candidates run 2-3 processes at once, speed outperforms another 2,000 PLN. Deciding after 10+ days costs about half the finalists.",
              },
              {
                title: "A range in the first message",
                meta: "Roughly doubles reply rates",
                body: "\"Competitive salary\" is a warning sign in Warsaw. A concrete range filters mismatches in 30 seconds.",
              },
              {
                title: "A shorter process",
                meta: "2-3 meetings, not 5",
                body: "Every extra stage drops 15-20% of the funnel. Take-home tasks over three hours effectively exclude passive candidates.",
              },
              {
                title: "A concrete scope",
                meta: "What you own, not \"interesting challenges\"",
                body: "Describing the real problem - a migration, scaling, a new team, an R&D budget - separates your offer from ten similar ones that week.",
              },
            ],
          },
        ],
      },
      {
        id: "proces",
        eyebrow: "04 / Process",
        title: "Timeline: from brief to start date",
        blocks: [
          {
            kind: "table",
            caption: "Typical IT hiring timeline in Warsaw",
            head: ["Stage", "Duration", "Notes"],
            rows: [
              ["Brief and range calibration", "2 - 3 days", "Checking the budget against the live market"],
              ["Direct search and screening", "5 - 10 days", "120-180 mapped profiles for a senior role"],
              ["Shortlist", "within 14 days of brief", "4 - 6 decision-ready candidates"],
              ["Client-side process", "5 - 12 days", "Two or three meetings is optimal"],
              ["Offer and decision", "3 - 5 days", "A counter-offer from the current employer is normal"],
              ["Start", "+1 - 3 months (employment) or 2 - 4 weeks (B2B)", "Plan contact during the notice period"],
            ],
          },
          {
            kind: "p",
            text: "Counter-offers are the norm in Warsaw, not the exception. Discussing the real reasons for changing jobs early - rather than at offer stage - is the cheapest way to avoid losing the candidate in the final week.",
          },
        ],
      },
    ],
    faqTitle: "FAQ: IT recruitment in Warsaw",
    faq: [
      {
        q: "What does a senior developer earn in Warsaw in 2026?",
        a: "Usually 18,000-28,000 PLN gross on an employment contract or 22,000-34,000 PLN net on B2B, depending on stack, system scale and sector.",
      },
      {
        q: "Why is hiring in Warsaw harder despite the largest talent pool?",
        a: "Because competition for attention is highest: candidates run 2-3 processes in parallel and also compare your offer with EUR-denominated remote jobs.",
      },
      {
        q: "Should the salary range be public?",
        a: "Yes. In our Warsaw processes, giving a range in the first message roughly doubles reply rates from passive candidates.",
      },
      {
        q: "How long does it take to fill an IT role in Warsaw?",
        a: "3-5 weeks from brief to accepted offer, plus the notice period: 1-3 months on employment or 2-4 weeks on B2B.",
      },
    ],
    cta: ctaEn,
    methodology:
      "Ranges come from Positive Recruitment processes run on the Warsaw market during 2025-2026 and are cross-checked against public Polish IT salary reports.",
  },
  zh: {
    title: "华沙IT招聘：薪酬基准与竞争格局",
    h1: "2026年华沙IT招聘：薪酬、竞争与流程",
    metaTitle: "华沙IT招聘2026：薪酬基准、人才池与周期",
    metaDescription:
      "2026年华沙IT招聘指南：波兰最高的薪酬区间、谁在争夺同一位候选人（金融科技、银行、成长型公司）、招聘需要多久，以及如何不靠加价赢得候选人。",
    eyebrow: "洞察 · 华沙",
    summary: "华沙设定了波兰的薪酬基准。谁在争夺候选人、高级工程师真实成本多少，以及什么决定了offer的胜负。",
    lead:
      "华沙是波兰最大的科技市场，也是全国薪酬区间的参照点。我们每天从克拉科夫为华沙市场做招聘，以下是2026年华沙流程的真实情况：还有谁在争夺你的候选人、薪资区间要定多高，以及为什么给钱最多的offer也会输。",
    note:
      "金额均为每月、以波兰兹罗提（PLN）计。雇佣合同（UoP）为税前工资（雇主成本约再加20%）,B2B为不含增值税的净发票金额。数据为我们2025-2026年流程的规划区间。",
    readTime: "阅读约 8 分钟",
    takeaways: [
      "华沙约有12万名IT专业人士——波兰最大的人才池，也是薪酬基准（100%）。",
      "华沙高级软件工程师：雇佣合同18,000-28,000 PLN税前，或B2B 22,000-34,000 PLN净额。",
      "银行和金融科技公司对同类岗位通常比市场平均高出10%-15%。",
      "华沙候选人通常同时推进2-3个流程，因此5个工作日内做决定，比把offer加2,000 PLN更有效。",
      "在首次联系时给出薪资区间，可使被动候选人的回复率大约翻倍。",
      "从简报到接受offer为3-5周；雇佣合同的通知期还会额外增加1-3个月。",
    ],
    sections: [
      {
        id: "konkurencja",
        eyebrow: "01 / 竞争",
        title: "在华沙你真正的竞争对手是谁",
        blocks: [
          {
            kind: "table",
            caption: "华沙IT市场的雇主类型,2026",
            head: ["雇主类型", "薪酬水平", "优势", "劣势"],
            rows: [
              ["银行与金融科技", "+10% 至 +15%", "预算、稳定性、数据规模", "流程长、合规负担、遗留系统"],
              ["产品型成长公司", "市场水平至 +10%", "产品影响力、节奏快、期权", "风险高、福利较薄"],
              ["全球研发中心", "市场水平", "品牌、福利、规范流程", "决策慢、职责范围窄"],
              ["软件公司 / 咨询", "-5% 至 -10%", "项目多样、上手快", "项目更替频繁、需驻场"],
              ["境外远程雇主（欧元）", "+15% 至 +30%", "薪酬高、完全远程", "时区问题、缺少本地团队"],
              ["公共部门", "-10% 至 -20%", "稳定、使命感", "薪酬区间、节奏、技术栈"],
            ],
          },
          {
            kind: "p",
            text: "华沙候选人拿来对标你的，不是本地市场，而是以欧元计价的远程职位。这并不意味着你必须出更高的价，而是你需要一个清晰的理由说明这个岗位更有意思：职责范围、系统规模、要解决的具体问题。",
          },
        ],
      },
      {
        id: "widelki",
        eyebrow: "02 / 薪酬",
        title: "在华沙雇一名工程师要多少钱",
        blocks: [
          {
            kind: "table",
            caption: "华沙薪酬区间,2026",
            head: ["岗位", "雇佣合同（税前/月）", "B2B（净额/月）", "B2B 小时费率"],
            rows: [
              ["中级软件工程师", "12,000 - 18,500 PLN", "14,000 - 22,000 PLN", "105 - 150 PLN"],
              ["高级软件工程师", "18,000 - 28,000 PLN", "22,000 - 34,000 PLN", "160 - 240 PLN"],
              ["高级 DevOps / SRE", "22,000 - 30,000 PLN", "26,000 - 36,000 PLN", "190 - 250 PLN"],
              ["高级数据工程师", "21,000 - 30,000 PLN", "25,000 - 35,000 PLN", "180 - 245 PLN"],
              ["ML / GenAI 工程师（高级）", "24,000 - 34,000 PLN", "28,000 - 40,000 PLN", "200 - 280 PLN"],
              ["工程经理", "26,000 - 38,000 PLN", "30,000 - 44,000 PLN", "-"],
            ],
          },
          {
            kind: "p",
            text: "在华沙，只有当你能说明岗位级别的真实含义（领域所有权、流量规模、架构影响力）时，区间上半部分才成立。仅凭“高级”这个头衔已经打动不了任何人——这里的候选人会先问决策范围，再问薪酬。",
          },
        ],
      },
      {
        id: "oferta",
        eyebrow: "03 / offer",
        title: "在华沙，什么决定offer的胜负",
        blocks: [
          {
            kind: "cards",
            items: [
              {
                title: "决策速度",
                meta: "最后一轮面试后5个工作日内",
                body: "在候选人同时推进2-3个流程的市场里，速度比再加2,000 PLN更有效。超过10天再决定，大约会失去一半决赛候选人。",
              },
              {
                title: "首次联系就给薪资区间",
                meta: "回复率大约翻倍",
                body: "在华沙，“具有竞争力的薪酬”是一个警示信号。具体区间能在30秒内筛掉不匹配。",
              },
              {
                title: "更短的流程",
                meta: "2-3轮面试，而不是5轮",
                body: "每增加一个环节，漏斗就会流失15%-20%。超过3小时的家庭作业几乎会把被动候选人全部排除。",
              },
              {
                title: "具体的职责范围",
                meta: "你拥有什么，而不是“有趣的挑战”",
                body: "描述真实问题——系统迁移、扩容、组建新团队、研发预算——才能让你的offer区别于同一周里另外十个类似机会。",
              },
            ],
          },
        ],
      },
      {
        id: "proces",
        eyebrow: "04 / 流程",
        title: "时间表：从简报到入职",
        blocks: [
          {
            kind: "table",
            caption: "华沙典型IT招聘时间表",
            head: ["阶段", "时长", "说明"],
            rows: [
              ["简报与薪酬校准", "2 - 3 天", "核对预算与真实市场是否匹配"],
              ["直接寻访与初步沟通", "5 - 10 天", "高级岗位通常梳理120-180份档案"],
              ["候选名单", "简报后14天内", "4 - 6 位可直接决策的候选人"],
              ["客户方流程", "5 - 12 天", "2-3轮面试最理想"],
              ["发offer与决策", "3 - 5 天", "现雇主给出挽留offer是常态"],
              ["入职", "+1 - 3 个月（雇佣合同）或 2 - 4 周（B2B）", "建议在通知期内保持联系"],
            ],
          },
          {
            kind: "p",
            text: "在华沙，挽留offer是常态而非例外。在流程早期而不是发offer时讨论候选人真正想换工作的原因，是避免在最后一周失去他的最省成本的方法。",
          },
        ],
      },
    ],
    faqTitle: "常见问题：华沙IT招聘",
    faq: [
      {
        q: "2026年华沙高级开发者的薪酬是多少？",
        a: "通常为雇佣合同18,000-28,000 PLN税前，或B2B 22,000-34,000 PLN净额，具体取决于技术栈、系统规模和所在行业。",
      },
      {
        q: "为什么人才池最大的华沙反而更难招人？",
        a: "因为争夺注意力的竞争最激烈：候选人同时推进2-3个流程，并且还会拿你的offer与以欧元计价的远程职位比较。",
      },
      {
        q: "职位信息里应该写明薪资区间吗？",
        a: "应该。在我们的华沙流程中，首次联系就给出区间，能让被动候选人的回复率大约翻倍。",
      },
      {
        q: "在华沙填补一个IT岗位需要多久？",
        a: "从简报到接受offer为3-5周，再加上通知期：雇佣合同1-3个月,B2B为2-4周。",
      },
    ],
    cta: ctaZh,
    methodology:
      "上述区间来自 Positive Recruitment 于2025-2026年在华沙市场开展的招聘流程，并与波兰IT行业公开薪酬报告相互印证。",
  },
};
